import React, { useState, useRef } from 'react';

interface FileUploadSectionProps {
  onFileUpload: (files: File[]) => Promise<boolean | void>;
  loading: boolean;
  uploadedFiles: File[];
  onClearResults: () => void;
  hasResults: boolean;
}

const FileUploadSection: React.FC<FileUploadSectionProps> = ({
  onFileUpload,
  loading,
  uploadedFiles,
  onClearResults,
  hasResults
}) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Prevent default drag behaviors on the entire document
  React.useEffect(() => {
    const preventDefaults = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
    };

    // Add event listeners to prevent default browser behavior
    document.addEventListener('dragenter', preventDefaults, false);
    document.addEventListener('dragleave', preventDefaults, false);
    document.addEventListener('dragover', preventDefaults, false);
    document.addEventListener('drop', preventDefaults, false);

    return () => {
      document.removeEventListener('dragenter', preventDefaults, false);
      document.removeEventListener('dragleave', preventDefaults, false);
      document.removeEventListener('dragover', preventDefaults, false);
      document.removeEventListener('drop', preventDefaults, false);
    };
  }, []);

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev + 1);
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragCounter(prev => prev - 1);
    if (dragCounter <= 1) {
      setDragActive(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    setDragCounter(0);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const files = Array.from(e.dataTransfer.files);
      // Filter files by supported types
      const supportedFiles = files.filter(file => {
        const supportedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
        return supportedTypes.includes(file.type);
      });
      
      if (supportedFiles.length !== files.length) {
        // Some files were filtered out
        const unsupportedCount = files.length - supportedFiles.length;
        alert(`${unsupportedCount} file(s) were skipped due to unsupported format.`);
      }
      
      setSelectedFiles(prev => [...prev, ...supportedFiles]);
      e.dataTransfer.clearData();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  const handleUpload = async () => {
    if (selectedFiles.length > 0) {
      const success = await onFileUpload(selectedFiles);
      // Clear selected files after successful processing
      if (success !== false) {
        setSelectedFiles([]);
      }
    }
  };

  const handleRemoveFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleClear = () => {
    setSelectedFiles([]);
    onClearResults(); // Clear both selected files and results
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="card upload-section">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <h3 className="card-title">
            <i className="fas fa-cloud-upload-alt mr-2"></i>
            Upload Student Worksheets
          </h3>
          {hasResults && (
            <button
              type="button"
              className="btn btn-sm btn-outline-light"
              onClick={handleClear}
              disabled={loading}
            >
              <i className="fas fa-trash mr-1"></i>
              Clear All
            </button>
          )}
        </div>
      </div>
      
      <div className="card-body">
        <div className="row">
          {/* Dropzone - Left Side */}
          <div className={selectedFiles.length > 0 ? 'col-md-6' : 'col-12'}>
            <div
              className={`auto-marking-dropzone ${dragActive ? 'active' : ''} ${loading ? 'disabled' : ''}`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleBrowseClick}
              style={{
                border: '2px dashed #dee2e6',
                borderRadius: '6px',
                padding: '1rem',
                textAlign: 'center' as const,
                backgroundColor: dragActive ? '#d4edda' : '#f8f9fa',
                borderColor: dragActive ? '#28a745' : '#dee2e6',
                transition: 'all 0.3s ease',
                cursor: loading ? 'not-allowed' : 'pointer',
                minHeight: '100px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: loading ? 0.6 : 1
              }}
            >
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".png,.jpg,.jpeg,.gif,.bmp,.pdf,.doc,.docx"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
              />
              <div className="dropzone-content" style={{ pointerEvents: 'none', width: '100%' }}>
                <i className="fas fa-cloud-upload-alt" style={{ 
                  fontSize: '1.5rem', 
                  color: '#6c757d', 
                  marginBottom: '0.5rem',
                  display: 'block'
                }}></i>
                <div style={{ 
                  color: '#495057', 
                  marginBottom: '0.25rem',
                  fontWeight: 500,
                  fontSize: '0.95rem'
                }}>
                  {dragActive
                    ? 'Drop files here'
                    : 'Click to select or drag & drop'}
                </div>
                <small style={{ color: '#6c757d', fontSize: '0.85rem' }}>
                  JPG, JPEG, PNG only (Max 5MB)
                </small>
              </div>
            </div>
          </div>

          {/* Selected Files - Right Side */}
          {selectedFiles.length > 0 && (
            <div className="col-md-6">
              <div className="selected-files-vertical">
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <h6 className="mb-0">Selected Files ({selectedFiles.length})</h6>
                </div>
                <div className="files-list-vertical">
                  {selectedFiles.map((file, index) => (
                    <div key={index} className="file-item-horizontal">
                      <div className="file-icon-small">
                        <i className={`fas ${
                          file.type.startsWith('image/') ? 'fa-image' :
                          file.type === 'application/pdf' ? 'fa-file-pdf' :
                          'fa-file-alt'
                        }`}></i>
                      </div>
                      <div className="file-details-horizontal">
                        <div className="file-name-small" title={file.name}>{file.name}</div>
                        <div className="file-size-small">{formatFileSize(file.size)}</div>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-danger p-0"
                        onClick={() => handleRemoveFile(index)}
                        disabled={loading}
                        style={{ minWidth: '20px' }}
                      >
                        <i className="fas fa-times"></i>
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Upload Button */}
        {selectedFiles.length > 0 && (
          <div className="upload-actions mt-3 text-center">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2"></span>
                  Processing...
                </>
              ) : (
                <>
                  <i className="fas fa-robot mr-2"></i>
                  Start Auto Marking ({selectedFiles.length} file{selectedFiles.length !== 1 ? 's' : ''})
                </>
              )}
            </button>
          </div>
        )}

        {/* Processing Status */}
        {loading && uploadedFiles.length > 0 && (
          <div className="alert alert-info mt-3 mb-0">
            <i className="fas fa-spinner fa-spin mr-2"></i>
            <strong>Processing files...</strong> Please wait while we analyze the uploaded worksheets.
          </div>
        )}
      </div>
    </div>
  );
};

export default FileUploadSection;